import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import { siteNavItems } from '@/components/feature/siteNav';
import { applyNoIndex } from '@/utils/seo';

const quickLinks = [
  {
    to: '/how-it-works',
    icon: 'ri-flow-chart',
    label: 'How It Works',
    desc: 'The decision loop behind every trade',
  },
  {
    to: '/gold-strategy',
    icon: 'ri-focus-3-line',
    label: 'Gold Strategy',
    desc: 'Entry logic, market read and risk model',
  },
  {
    to: '/trade-journal',
    icon: 'ri-book-read-line',
    label: 'Trade Journal',
    desc: 'Every recorded entry and exit',
  },
  {
    to: '/mt5-setup',
    icon: 'ri-settings-3-line',
    label: 'MT5 Setup',
    desc: 'Install, configure and run the EA',
  },
];

export default function NotFound() {
  const location = useLocation();

  useEffect(() => {
    const removeNoIndex = applyNoIndex();
    return removeNoIndex;
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader current="" />

      <main className="flex-1 pt-[76px]">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 grid-lines opacity-60 pointer-events-none"></div>
          <div className="absolute -top-20 right-[-8%] w-[520px] h-[520px] rounded-full bg-accent-200/40 blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-[-30%] left-[-10%] w-[440px] h-[440px] rounded-full bg-primary-100/50 blur-3xl pointer-events-none"></div>

          <div className="relative w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 max-w-[1440px] mx-auto">
            <div className="max-w-[720px] mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-3 h-8 rounded-full border border-primary-300 bg-primary-50 mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                <span className="text-[11px] font-data tracking-[0.18em] text-primary-700">
                  SIGNAL LOST · 404
                </span>
              </div>

              <div className="relative flex items-end justify-center select-none pointer-events-none">
                <span className="font-heading font-extrabold text-foreground-950 tracking-tighter leading-none text-[120px] sm:text-[168px] lg:text-[196px]">
                  4
                </span>
                <span className="w-[86px] h-[86px] sm:w-[118px] sm:h-[118px] lg:w-[138px] lg:h-[138px] mb-4 sm:mb-6 lg:mb-7 rounded-full bg-foreground-950 flex items-center justify-center mx-1 sm:mx-2">
                  <i className="ri-line-chart-line text-primary-400 text-[40px] sm:text-[56px] lg:text-[66px]"></i>
                </span>
                <span className="font-heading font-extrabold text-foreground-950 tracking-tighter leading-none text-[120px] sm:text-[168px] lg:text-[196px]">
                  4
                </span>
              </div>

              <h1 className="mt-8 font-heading font-extrabold text-foreground-950 tracking-tight text-[28px] sm:text-[36px] lg:text-[40px]">
                This price level doesn&apos;t exist.
              </h1>

              <p className="mt-4 text-[15px] md:text-base leading-relaxed text-foreground-600 max-w-[520px] mx-auto">
                The page you&apos;re looking for slipped outside the range. Check the address, or
                jump back to a level we do trade.
              </p>

              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3.5 py-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-links-line text-[13px] text-foreground-500"></i>
                </span>
                <span className="text-[12px] font-data tracking-wide text-foreground-600 max-w-[320px] truncate">
                  {location.pathname}
                </span>
              </div>

              <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-[15px] font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer hover:-translate-y-0.5"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </span>
                  Back to Home
                </Link>
                <Link
                  to="/trade-journal"
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border border-foreground-300 text-foreground-950 text-[15px] font-semibold whitespace-nowrap hover:border-primary-400 hover:text-primary-700 transition-all duration-200 cursor-pointer hover:-translate-y-0.5"
                >
                  View Trade Journal
                </Link>
              </div>
            </div>

            <div className="mt-16 md:mt-20 max-w-[1100px] mx-auto">
              <div className="flex items-center gap-4 mb-6">
                <span className="text-[11px] font-data tracking-[0.22em] text-foreground-500 whitespace-nowrap">
                  OR PICK A DESTINATION
                </span>
                <span className="h-px flex-1 bg-background-300"></span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {quickLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="group flex flex-col gap-4 rounded-lg border border-background-300 bg-background-100 hover:border-primary-300 hover:bg-background-50 p-5 transition-all duration-200 cursor-pointer hover:-translate-y-0.5"
                  >
                    <span className="w-11 h-11 rounded-md bg-background-50 border border-background-300 group-hover:bg-primary-100 group-hover:border-primary-200 flex items-center justify-center flex-none transition-colors duration-200">
                      <i className={`${item.icon} text-[20px] text-primary-700`}></i>
                    </span>
                    <span className="flex flex-col gap-1.5">
                      <span className="flex items-center gap-2 font-heading font-bold text-[15px] text-foreground-950 whitespace-nowrap">
                        {item.label}
                        <span className="w-4 h-4 flex items-center justify-center text-primary-600 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200">
                          <i className="ri-arrow-right-line text-[15px]"></i>
                        </span>
                      </span>
                      <span className="text-[13px] leading-snug text-foreground-600">
                        {item.desc}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
                {siteNavItems.map((item) => (
                  <Link
                    key={item.key}
                    to={item.to}
                    className="inline-flex items-center gap-1.5 text-[13px] text-foreground-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-corner-down-right-line text-[13px] text-primary-500"></i>
                    </span>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter current="" />
    </div>
  );
}